/**
 * Custom hook for submitting feedback with optimistic updates
 * Integrates TanStack Query mutation with proper error handling and UI feedback
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  feedbackMutationOptions,
  FeedbackRequest,
  FeedbackResponse,
  FeedbackError,
  feedbackUtils
} from '../api/feedback';
import { ApiError, isAuthenticationError } from '../api/base/errors';
import { extractUsername } from '../api/auth';
import { showNotification } from '../components/Notification';
import { getPrimaryGenieSpaceName } from '../utils/genie';


/**
 * Optimistic update context for rollback on error
 */
interface FeedbackMutationContext {
  messageId: string;
  feedbackType: 'positive' | 'negative';
  previousOptimisticState?: any; // For potential future message action state
}

/**
 * Core mutation hook for submitting feedback
 * Follows TanStack Query best practices with optimistic updates
 */
export function useFeedbackMutation(messageId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    ...feedbackMutationOptions(messageId),

    // Optimistic updates for immediate UI feedback
    onMutate: async (variables: FeedbackRequest): Promise<FeedbackMutationContext> => {
      // Cancel any outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({
        queryKey: ['conversations']
      });

      // Store context for potential rollback
      const context: FeedbackMutationContext = {
        messageId: variables.responseID,
        feedbackType: variables.feedbackType,
      };

      // Future: Add optimistic updates to message actions state
      // For now, we'll rely on UI state in components

      return context;
    },

    // Success: Confirm feedback was submitted
    onSuccess: (data: FeedbackResponse, _variables: FeedbackRequest, context: FeedbackMutationContext) => {

      // Future: Update conversation cache with feedback metadata
      // For now, the optimistic UI updates in components are sufficient

      // notify the user that the feedback was successful
      showNotification('Thank You for helping to improve Co-op GPT');
    },

    // Error: Rollback optimistic updates and handle error
    onError: (error: Error, _variables: FeedbackRequest, context?: FeedbackMutationContext) => {
      // Log error for debugging
      console.error('Feedback submission failed:', {
        messageId: context?.messageId,
        feedbackType: context?.feedbackType,
        error: error.message,
      });

      // Future: Rollback any optimistic updates
      // For now, error handling is done in components
    },

    // Enhanced retry configuration following TanStack Query best practices
    retry: (failureCount: number, error: any) => {
      // Don't retry authentication errors - user needs to re-auth
      if (isAuthenticationError(error)) {
        return false;
      }

      // Don't retry validation errors (4xx) - data is invalid
      if (error instanceof ApiError && error.status && error.status >= 400 && error.status < 500) {
        return false;
      }

      // Don't retry feedback-specific errors that aren't retryable
      if (error instanceof FeedbackError && !error.isRetryable) {
        return false;
      }

      // Retry network errors and server errors up to 3 times
      return failureCount < 3;
    },

    // Exponential backoff retry delay (1s, 2s, 4s, max 30s)
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * High-level hook that provides a clean interface for feedback submission
 * Integrates validation and error handling
 */
export function useFeedbackSubmission(messageId: string) {
  const feedbackMutation = useFeedbackMutation(messageId);

  const submitFeedbackWithValidation = useCallback(async (
    request: FeedbackRequest
  ) => {
    // Validate request data
    const validation = feedbackUtils.validateFeedbackRequest(request);
    if (!validation.isValid) {
      throw new FeedbackError(
        `Validation failed: ${validation.errors.join(', ')}`,
        400,
        'VALIDATION_ERROR'
      );
    }

    // Get user details from MS authentication
    const username = extractUsername();

    // Add authenticated username if available
    const requestWithUserName: FeedbackRequest = {
      ...request,
      ...(username && { userName: username }),
    };

    try {
      const response = await feedbackMutation.mutateAsync(requestWithUserName);
      return { success: true, response, error: null };
    } catch (error) {
      return {
        success: false,
        response: null,
        error: error instanceof Error ? error.message : 'Failed to submit feedback'
      };
    }
  }, [feedbackMutation]);

  return {
    submitFeedback: submitFeedbackWithValidation,
    isLoading: feedbackMutation.isPending,
    error: feedbackMutation.error,
    isSuccess: feedbackMutation.isSuccess,
    isError: feedbackMutation.isError,
    reset: feedbackMutation.reset,

    // Helper computed properties
    canSubmit: !feedbackMutation.isPending,

    // Direct access to mutation for advanced use cases
    mutation: feedbackMutation,
  };
}

/**
 * Genie metadata interface for feedback
 */
interface GenieMetadata {
  genie_space_names?: string[];
  genie_conversation_id?: string;
  genie_message_id?: string;
}

/**
 * Simplified hook for quick feedback submission without complex validation
 * Useful for simple thumbs up/down actions
 */
export function useQuickFeedback(messageId: string, conversationId: string) {
  const { submitFeedback, isLoading, error, isSuccess, reset } = useFeedbackSubmission(messageId);

  const submitQuickFeedback = useCallback(async (
    feedbackType: 'positive' | 'negative',
    comment?: string,
    shareData: boolean = false,
    genieMetadata?: GenieMetadata
  ) => {
    const baseRequest = feedbackUtils.createBaseFeedbackRequest(
      messageId,
      feedbackType,
      conversationId,
      shareData
    );

    const fullRequest: FeedbackRequest = {
      ...baseRequest,
      ...(comment && { comment: comment.trim() }),
    };

    const genieSpaceName = getPrimaryGenieSpaceName(genieMetadata?.genie_space_names);
    if (genieSpaceName && genieMetadata?.genie_conversation_id && genieMetadata?.genie_message_id) {
      fullRequest.genie_space_name = genieSpaceName;
      fullRequest.genie_conversation_id = genieMetadata.genie_conversation_id;
      fullRequest.genie_message_id = genieMetadata.genie_message_id;
    }

    return await submitFeedback(fullRequest);
  }, [messageId, conversationId, submitFeedback]);

  return {
    submitQuickFeedback,
    isLoading,
    error,
    isSuccess,
    reset,
    canSubmit: !isLoading,
  };
}

/**
 * Hook for detailed feedback submission with full form data
 * Used by feedback modals with detailed forms
 */
export function useDetailedFeedback(
  messageId: string,
  conversationId: string
) {
  const { submitFeedback, isLoading, error, isSuccess, reset } = useFeedbackSubmission(messageId);

  const submitDetailedFeedback = useCallback(async (
    feedbackType: 'positive' | 'negative',
    formData: {
      issue?: string;
      details: string;
      shareData: boolean;
    },
    messageContent?: string,
    userQuery?: string,
    genieMetadata?: GenieMetadata
  ) => {
    const baseRequest = feedbackUtils.createBaseFeedbackRequest(
      messageId,
      feedbackType,
      conversationId,
      formData.shareData
    );

    const fullRequest: FeedbackRequest = {
      ...baseRequest,
      comment: formData.details.trim(),
      ...(formData.issue && { issue: formData.issue }),

      // Include query and answer only if user consents to sharing
      ...(formData.shareData && userQuery && { query: userQuery }),
      ...(formData.shareData && messageContent && { answer: messageContent }),
    };

    const genieSpaceName = getPrimaryGenieSpaceName(genieMetadata?.genie_space_names);
    if (genieSpaceName && genieMetadata?.genie_conversation_id && genieMetadata?.genie_message_id) {
      fullRequest.genie_space_name = genieSpaceName;
      fullRequest.genie_conversation_id = genieMetadata.genie_conversation_id;
      fullRequest.genie_message_id = genieMetadata.genie_message_id;
    }

    return await submitFeedback(fullRequest);
  }, [messageId, conversationId, submitFeedback]);

  return {
    submitDetailedFeedback,
    isLoading,
    error,
    isSuccess,
    reset,
    canSubmit: !isLoading,
  };
}
